// steps/BauhofStep.tsx (converted to React web with MUI)

import React, { useEffect } from "react";
import { CircularProgress, Box, Typography, List, ListItemButton, ListItemText, Divider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useQuery } from "@tanstack/react-query";

import { useAllowScroll } from "../AllowScrollContext";
import { truckTypeLabel } from "./shared";
import { MaterialOrder } from "@/components/planning/Project";
import ApiService from "@/api/ApiService";
import { ActionItem } from "@/shared/sharedService/ActionItem";

const orderToListItem = (order: MaterialOrder) => {
  const isBaustellendienst = order.amount === -1 && !order.article;
  const isBauhof = !!order.article && order.article.bauhof > 0;

  const getMengePart = () => {
    if (isBauhof || isBaustellendienst || order.amount <= 0) return "";
    return `${order.amount} ${order.roundsUnit ? (order.amount > 1 ? "Touren" : "Tour") : (order.article?.unit ?? "t")}`;
  };

  const getMaterialPart = () => {
    if (isBaustellendienst) return "Nur Baustellendienst";
    if (!order.article) return "Kein Material";
    if (isBauhof) {
      const additionalCount = order.additionalArticles.length;
      return `${order.amount}x ${order.article.name}${additionalCount > 0 ? ` +${additionalCount} weitere` : ""}`;
    }
    return `${order.article.name} - ${order.article.plantName} (${order.article.number})`;
  };

  const getTimePart = () => {
    if (!order.firstUnload) return "";
    if (order.firstUnload.startsWith("x")) {
      if (order.firstUnload.startsWith("xB")) return xBFormatter(order.firstUnload, "BS", true);
      if (order.firstUnload === "xAM") return "vormittags";
      if (order.firstUnload === "xPM") return "nachmittags";
      return "";
    }
    return `${order.firstUnload} Uhr`;
  };

  const getTruckPart = () => {
    if (order.transportInformation.length === 0) return "ohne LKW";
    return order.transportInformation
      .map((ti) => {
        const options = [
          ti.thermo ? "Thermo" : "",
          ti.bordmatik ? "Bordm." : "",
          ti.stahlmulde ? "Stahl" : "",
          ti.allrad ? "Allr." : "",
          ti.siteWork ? "Bstd." : "",
        ]
          .filter(Boolean)
          .join(" ");
        const truckLabel = `${ti.amount ?? 0}x ${truckTypeLabel(ti.type)}`;
        return options ? `${truckLabel} ${options}` : truckLabel;
      })
      .join(", ");
  };

  const mengePart = getMengePart();
  const materialPart = getMaterialPart();
  const timePart = getTimePart();
  const truckPart = getTruckPart();

  const title = `${mengePart}${mengePart ? " " : ""}${materialPart}`;
  const subTitle = `${timePart ? timePart + " - " : ""}${truckPart}`;

  return { title, subTitle };
};

export default function HistoryStep({
  projectId,
  order,
  onBack,
  onSelect,
}: {
  projectId: number;
  order: MaterialOrder;
  onBack: () => void;
  onSelect: (patch: Partial<MaterialOrder>) => void;
}) {
  const { setAllowScroll } = useAllowScroll();

  useEffect(() => {
    setAllowScroll(true);
  }, [setAllowScroll]);

  const historyQuery = useQuery<MaterialOrder[]>({
    queryKey: ["getMOHistory", projectId],
    queryFn: ApiService.getMaterialOrderHistory(projectId),
  });

  if (historyQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (historyQuery.isError) {
    return <Typography>Fehler beim Laden der Historie</Typography>;
  }

  const history = historyQuery.data ?? [];

  const handleSelectHistory = (selected: MaterialOrder) => {
    const { id, orderSentAt, orderSentBy, job, plantStatus, transportStatus, deleted, ...patch }: Partial<MaterialOrder> = selected;
    onSelect(patch);
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: 18 }}>Vorherige Bestellung kopieren</Typography>
      </Box>

      <Box>
        <Box sx={{ mb: 2, display: "flex" }}>
          {/* If ActionItem is RN-only, replace with an MUI Button/IconButton later */}
          <ActionItem icon={ArrowBackIcon as any} label={"zurück"} color={undefined} onPress={handleBack} />
        </Box>

        <List sx={{ mt: 1, borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
          {history.map((o, i) => {
            const { title, subTitle } = orderToListItem(o);
            return (
              <React.Fragment key={i}>
                <ListItemButton onClick={() => handleSelectHistory(o)} sx={{ py: 1.25 }}>
                  <ListItemText primary={title} secondary={subTitle} />
                  <ChevronRightIcon />
                </ListItemButton>
                {i < history.length - 1 ? <Divider /> : null}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}
